import { useEffect, useState } from 'react';
import { App, Button, Drawer, Flex, Typography } from 'antd';
import { useCreateCalculation, useUpdateCalculation } from '@/features/agreements/hooks/useCalculations';
import { getErrorMessage } from '@/shared/utils/errors/getErrorMessage';
import { ExpressionNodeEditor } from './ExpressionNodeEditor';
import { DEFAULT_ROOT_EXPRESSION, CHILDREN_RULES } from './constants';

const { Text } = Typography;

const validateExpression = (node) => {
  if (!node) return false;
  if (node.nodeType === 'value') {
    if (node.valueType === 'fixed') {
      return !!(node.valueName?.trim() && (node.fixedValue != null && node.fixedValue !== ''));
    }
    return !!node.valueType;
  }
  if (node.nodeType === 'operation') {
    const rule = CHILDREN_RULES[node.operationType];
    if (!rule || !node.operationType) return false;
    const children = node.children ?? [];
    if (rule.exact != null && children.length !== rule.exact) return false;
    if (rule.min != null && children.length < rule.min) return false;
    return children.every((child) => validateExpression(child));
  }
  return false;
};

export const PremiumCalculationDrawer = ({
  open,
  onClose,
  agreementId,
  calculation,
  onSave,
}) => {
  const { notification } = App.useApp();
  const [expression, setExpression] = useState(null);
  const isEditMode = !!calculation;

  const createMutation = useCreateCalculation();
  const updateMutation = useUpdateCalculation();

  useEffect(() => {
    if (open) {
      if (isEditMode && calculation?.expression) {
        setExpression(JSON.parse(JSON.stringify(calculation.expression)));
      } else {
        setExpression(JSON.parse(JSON.stringify(DEFAULT_ROOT_EXPRESSION)));
      }
    }
  }, [open, isEditMode, calculation?.expression]);

  const handleSubmit = async () => {
    if (!expression) return;
    if (!validateExpression(expression)) {
      notification.warning({
        message: <Text strong>Expresión incompleta</Text>,
        description: 'Completa todos los campos y asegúrate de que cada operación tenga el número correcto de operandos.',
      });
      return;
    }

    try {
      if (isEditMode) {
        await updateMutation.mutateAsync({
          agreementId,
          calculationId: calculation.id,
          data: { expression },
        });
        notification.success({
          message: <Text strong>Cálculo actualizado</Text>,
          description: 'El cálculo de prima se ha actualizado correctamente.',
        });
      } else {
        await createMutation.mutateAsync({
          agreementId,
          data: { calculationType: 'premium', expression },
        });
        notification.success({
          message: <Text strong>Cálculo creado</Text>,
          description: 'El cálculo de prima se ha creado correctamente.',
        });
      }
      onSave?.();
      onClose?.();
    } catch (error) {
      notification.error({
        message: <Text strong>{isEditMode ? 'Error al actualizar' : 'Error al crear'}</Text>,
        description: getErrorMessage(error),
      });
    }
  };

  const handleClose = () => {
    setExpression(null);
    onClose?.();
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Drawer
      title={isEditMode ? 'Editar cálculo de prima' : 'Definir cálculo de prima'}
      open={open}
      onClose={handleClose}
      width='36em'
      destroyOnClose
      footer={
        <Flex gap='small' justify='end'>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button type='primary' onClick={handleSubmit} loading={isSaving}>
            {isEditMode ? 'Guardar' : 'Crear'}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap='middle'>
        <Text type='secondary'>
          La expresión define cómo se calcula la prima. Usa operaciones (suma, multiplicación, etc.) y valores (valor asegurado, tasa, IVA o un valor fijo).
        </Text>
        {expression && (
          <ExpressionNodeEditor
            node={expression}
            onChange={setExpression}
            isRoot={true}
            depth={0}
          />
        )}
      </Flex>
    </Drawer>
  );
};
